/* eslint-disable @typescript-eslint/naming-convention */
import { camelCase, difference, get, pick } from 'lodash';
import { filter, take } from 'rxjs/operators';
import URL from 'url-parse';

import type { ContentItem, Page } from '@swf/cms';
import type { EffectParams, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WidgetReference } from '@swf/core/types/Widget';
import { canUseDOM } from '@swf/core/utils';
import { compareCacheControl } from '@swf/core/utils/compareCacheControl';
import { getPageAndMenuResponses } from '@swf/core/utils/pages';
import { fetchProperties } from '@swf/core/utils/widgets';
import type widgets from '@swf/widgets/index';

import type {
  Alias,
  CMSState,
  MenuResponse,
  PageResponse,
  RoutesResponse,
  WebsiteResponse
} from './index';

const requestOverride = {
  timeout: 20000,
  retry: {
    count: 3
  }
};
const cache: {
  pages: Record<string, PageResponse>;
  menus: Record<string, Omit<MenuResponse, 'content'>>;
  lastContent: Record<string, ContentItem> | null;
} = {
  pages: {},
  menus: {},
  lastContent: null
};

const effects = {
  async fetchWebsite(payload: undefined, { request, dispatch, rootState }: EffectParams) {
    const payloadBody = getRequestPayload(select.location.get(rootState).href, rootState);
    if (payloadBody.siteId === 'isp') delete payloadBody.siteId;
    const [{ body: website }, { body: routes }] = await Promise.all([
      request<WebsiteResponse>({
        service: '/api/v1-preview/cms/website',
        ...requestOverride
      }),
      // TODO: CMS-migration - remove request to routes after all verticals are on ContentStack
      request<RoutesResponse>({
        service: '/api/v1-preview/cms/routes',
        body: payloadBody
      })
    ]);

    if (website) {
      // TODO: CMS-migration - legal_age should be a part of Website entry in ContentStack
      // remove request to session after ContentStack migration
      if (!website.website.legal_age) {
        const {
          body: { legalAge }
        } = await request({ service: 'session.info' });
        website.website.legal_age = legalAge;
      }

      // TODO: CMS-migration - aliases should be fixed on the middle tier,
      // fix the redirect logic in StarsPage.tsx
      const routesWithAliasKeys = Object.fromEntries(
        Object.entries(routes).map(([key, value]) => [
          camelCase(key),
          value.replace('/casino/help/', '/help/')
        ])
      );
      website.aliases = {
        ...(routesWithAliasKeys as Partial<{ [key in Alias]: string }>),
        ...website.aliases
      };
    }

    dispatch.cms.update(website);
  },
  async fetchPage({ href }: { href: string }, params: EffectParams) {
    const { rootState, dispatch, request } = params;
    const url = new URL(href, true);
    const pageCache = canUseDOM && cache.pages[href];
    const menuCache = canUseDOM && cache.menus[href];
    const payloadBody = getRequestPayload(href, rootState);
    let cacheControl: string | undefined;

    if (payloadBody.siteId) {
      url.set('query', { ...url.query, siteId: payloadBody.siteId });
    }

    dispatch.cms.setPageLoaded(false);

    const localPage = await getPageAndMenuResponses(rootState, payloadBody.path);

    const [page, menu] =
      localPage ??
      (await Promise.all([
        pageCache
          ? Promise.resolve(pageCache)
          : request<PageResponse>({
              service: 'cms.page',
              body: payloadBody,
              ...requestOverride
            }).then((response) => {
              if (canUseDOM) cache.pages[href] = response.body;

              if (response.headers.cacheControl) {
                cacheControl = compareCacheControl(response.headers.cacheControl, cacheControl);
              }
              return response.body;
            }),
        menuCache
          ? Promise.resolve({ ...menuCache, content: {} })
          : request<MenuResponse>({
              service: 'cms.navigation',
              body: payloadBody,
              ...requestOverride
            }).then((response) => {
              if (canUseDOM) {
                cache.menus[href] = { menu: response.body.menu, depth: response.body.depth };
              }
              if (response.headers.cacheControl) {
                cacheControl = compareCacheControl(response.headers.cacheControl, cacheControl);
              }
              return response.body;
            })
      ]));

    // TODO: CMS-migration - remove after stack content can be modified
    const defaultLayout = {
      layout_type: 'StarsLayout' as Page['layout']['layout_type'],
      background_image: undefined,
      background_color: '',
      background_fit: null
    };

    if (page?.page && !page.page.layout) page.page.layout = defaultLayout;
    // TODO: CMS-migration - end

    const content = { ...page?.content, ...menu?.content };

    const { lastContent } = cache;
    const diffContent =
      lastContent && canUseDOM
        ? pick(content, difference(Object.keys(content), Object.keys(lastContent)))
        : content;
    cache.lastContent = content;
    await effects.prepare(diffContent, params);

    dispatch.cms.update({
      ...page,
      ...menu,
      content,
      url,
      pathBrand: select.cms.getPathBrandId(rootState),
      cacheControl
    });

    await dispatch.cms.preload(diffContent);

    dispatch.cms.setPageLoaded(true);
  },
  async loadModel(
    { item, state }: { item: ContentItem; state?: unknown },
    { actions, addModel, dispatch }: EffectParams
  ) {
    const { uid, _content_type_uid } = item;
    const { model } = await fetchProperties(_content_type_uid as keyof typeof widgets);
    if (!model) return;
    const unmount = actions.pipe(
      filter(
        ({ payload, type }) => type === 'cms/update' && payload.url && !payload.content?.[uid]
      ),
      take(1)
    );
    const modelState = state ?? get(canUseDOM && window, ['__INITIAL_STATE__', uid], model.state);

    !dispatch[uid] &&
      addModel({
        ...model,
        state: modelState,
        name: uid,
        widget: { ...(item as WidgetReference), unmount }
      });
  },
  async prepare(data: CMSState['content'] | undefined, params: EffectParams) {
    await Promise.all(
      Object.values(data || select.cms.getContent(params.rootState)).map((widget) =>
        effects.loadModel({ item: widget }, params)
      )
    );
  },
  async preload(data: CMSState['content'] | undefined, { rootState, dispatch }: EffectParams) {
    await Promise.all(
      Object.values(data || select.cms.getContent(rootState)).map(({ uid }) => {
        if (canUseDOM && window.__INITIAL_STATE__) delete window.__INITIAL_STATE__[uid];
        const preload = dispatch[uid]?.preload?.().catch(() => undefined);
        return !canUseDOM ? preload : undefined;
      })
    );
  }
};

const getRequestPayload = (href: string, rootState: RootState) => {
  const url = new URL(href, true);
  const { pathname, query } = url;
  const path = select.location.stripLocale(rootState, pathname);
  const location = select.location.get(rootState);
  const siteId = query.siteId || location.query.siteId;
  const { clientVersion } = select.localSettings.get(rootState);
  return {
    path,
    clientVersion: canUseDOM ? window.version : clientVersion,
    siteId
  };
};

export default effects;
